import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Users, Film, Plus, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { countryFlag, countryName } from "@/lib/constants";
import ContentCard from "@/components/ContentCard";

export default function CreatorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [creator, setCreator] = useState(null);

  const load = () => api.get(`/creators/${id}`).then((r) => setCreator(r.data)).catch(() => setCreator(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (creator === false) return <Centered>Creator not found.</Centered>;
  if (!creator) return <Centered><div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></Centered>;

  const follow = async () => {
    if (!user) { toast.error("Please sign in to follow"); return; }
    try {
      const { data } = await api.post(`/creators/${id}/follow`);
      setCreator({ ...creator, following: data.following, follower_count: creator.follower_count + (data.following ? 1 : -1) });
      toast.success(data.following ? `Following ${creator.display_name}` : "Unfollowed");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="relative h-56 sm:h-72">
        {creator.banner ? <img src={creator.banner} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#E50914]/40 to-[#D4AF37]/20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#08070B] via-[#08070B]/40 to-transparent" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 -mt-16 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-5">
          {creator.avatar ? <img src={creator.avatar} alt={creator.display_name} className="w-28 h-28 rounded-2xl border-4 border-[#08070B] object-cover" />
            : <span className="w-28 h-28 rounded-2xl border-4 border-[#08070B] bg-[#D4AF37]/40 flex items-center justify-center text-4xl font-bold">{creator.display_name?.[0]}</span>}
          <div className="flex-1">
            <h1 className="font-display font-black text-3xl sm:text-4xl flex items-center gap-2">
              {creator.display_name} {creator.verified && <BadgeCheck className="w-6 h-6 text-[#E50914]" />}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-white/60">
              <span className="flex items-center gap-1">{countryFlag(creator.country)} {countryName(creator.country)}</span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {creator.follower_count?.toLocaleString()} followers</span>
              <span className="flex items-center gap-1"><Film className="w-4 h-4" /> {creator.content?.length || 0} titles</span>
            </div>
          </div>
          <button onClick={follow} data-testid="follow-btn"
            className={`px-6 py-2.5 rounded-full font-semibold flex items-center gap-2 transition-colors ${creator.following ? "bg-white/10 border border-white/20" : "bg-[#E50914] hover:bg-[#FF2A3B]"}`}>
            {creator.following ? <><Check className="w-4 h-4" /> Following</> : <><Plus className="w-4 h-4" /> Follow</>}
          </button>
        </div>

        <p className="text-white/70 max-w-3xl mt-5 leading-relaxed">{creator.bio}</p>

        <h2 className="font-display font-bold text-2xl mt-10 mb-4">Titles</h2>
        {creator.content?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="creator-content-grid">
            {creator.content.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
          </div>
        ) : <p className="text-white/40">No published titles yet.</p>}
      </div>
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-[60vh] flex items-center justify-center text-white/50">{children}</div>;
